import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Sparkles, X, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";
import { scheduleStreakReminder } from "../lib/notifications";

// Color/intensity ramps with streak length
const streakStyle = (n) => {
  if (n >= 100) return { bg: "from-rose-500 to-orange-500", glow: "shadow-rose-500/40", text: "text-white" };
  if (n >= 30)  return { bg: "from-orange-500 to-amber-400", glow: "shadow-orange-500/40", text: "text-white" };
  if (n >= 7)   return { bg: "from-amber-400 to-lime-400",   glow: "shadow-amber-400/30", text: "text-black" };
  if (n >= 1)   return { bg: "from-lime-400 to-emerald-500", glow: "shadow-lime-400/30", text: "text-black" };
  return { bg: "from-zinc-700 to-zinc-800", glow: "shadow-none", text: "text-zinc-400" };
};

export default function StreakBadge({ initial = null, pingOnMount = false }) {
  const [streak, setStreak] = useState(initial);
  const [celebrate, setCelebrate] = useState(false);
  const [milestoneN, setMilestoneN] = useState(0);
  const [passSaved, setPassSaved] = useState(false); // 4s celebratory strip
  const pingedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.streakGet();
        if (cancelled) return;
        setStreak(result);
        try { scheduleStreakReminder(result?.current_streak || 0); } catch {}
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [pingOnMount]);

  // Listen for streak updates from any ping across the app.
  useEffect(() => {
    const onPassUsed = (e) => {
      setStreak((prev) => ({ ...(prev || {}), ...(e.detail || {}) }));
      setPassSaved(true);
      // Auto-hide after 4.5s
      setTimeout(() => setPassSaved(false), 4500);
    };
    const onUpdated = (e) => {
      setStreak((prev) => ({ ...(prev || {}), ...(e.detail || {}) }));
    };
    window.addEventListener('streak:pass-used', onPassUsed);
    window.addEventListener('streak:updated', onUpdated);
    return () => {
      window.removeEventListener('streak:pass-used', onPassUsed);
      window.removeEventListener('streak:updated', onUpdated);
    };
  }, []);

  if (!streak) return null;
  const n = streak.current_streak || 0;
  const s = streakStyle(n);
  const next = streak.next_milestone;
  // Show ✿ marker when a pass was recently used (currently on 30-day cooldown)
  const passOnCooldown = streak.pass_available === false && !!streak.last_pass_used;

  const title = passOnCooldown
    ? `Life-happens pass used — refreshes in ${streak.days_until_pass || 30} day${streak.days_until_pass === 1 ? '' : 's'}`
    : (next ? `${next - n} day${next - n === 1 ? "" : "s"} to next milestone` : "Top tier streak");

  return (
    <>
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 14 }}
        data-testid="streak-badge"
        className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${s.bg} ${s.text} shadow-lg ${s.glow}`}
        title={title}
      >
        <motion.div
          animate={n > 0 ? { scale: [1, 1.15, 1], rotate: [-2, 2, -2] } : {}}
          transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
        >
          <Flame className="h-3.5 w-3.5" strokeWidth={2.5} />
        </motion.div>
        <span className="text-xs font-bold leading-none tabular-nums">{n}</span>
        {passOnCooldown && (
          <span
            data-testid="streak-pass-used-marker"
            aria-label="Life-happens pass used this cycle"
            className="ml-0.5 text-[11px] leading-none text-white/90 select-none"
          >
            ✿
          </span>
        )}
      </motion.div>

      {/* Small celebratory strip when the pass just saved the streak */}
      <AnimatePresence>
        {passSaved && (
          <motion.div
            key="pass-saved-strip"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            data-testid="streak-pass-saved-toast"
            className="fixed left-1/2 -translate-x-1/2 top-4 z-[80] flex items-center gap-2 rounded-full bg-zinc-900/95 border border-lime-500/30 pl-3 pr-4 py-2 shadow-2xl backdrop-blur"
          >
            <HeartHandshake className="h-4 w-4 text-lime-400" />
            <div className="text-xs text-zinc-200">
              <span className="font-semibold text-white">Life-happens pass used.</span>
              <span className="text-zinc-400"> Streak protected — one per 30 days.</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {celebrate && (
          <MilestoneModal n={milestoneN} longest={streak.longest_streak} onClose={() => setCelebrate(false)} />
        )}
      </AnimatePresence>
    </>
  );
}

function MilestoneModal({ n, longest, onClose }) {
  const titles = {
    3:   "Three days strong",
    7:   "A whole week",
    14:  "Two weeks deep",
    30:  "Thirty days",
    60:  "Two months in",
    100: "Triple digits",
    200: "Two hundred days",
    365: "A full year",
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center px-6"
      data-testid="streak-milestone-modal"
    >
      <motion.div
        initial={{ scale: 0.7, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
        transition={{ type: "spring", stiffness: 220, damping: 18 }}
        className="relative w-full max-w-sm bg-zinc-900 border border-orange-500/30 rounded-3xl p-7 text-center text-white"
      >
        <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full hover:bg-zinc-800" data-testid="streak-milestone-close">
          <X className="h-4 w-4" />
        </button>

        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 12 }}
          className="mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 grid place-items-center shadow-2xl shadow-orange-500/50"
        >
          <Flame className="h-10 w-10 text-white" strokeWidth={2.5} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="mt-5"
        >
          <div className="text-[11px] uppercase tracking-wider text-orange-300/80">Streak milestone</div>
          <h2 className="mt-1 text-3xl font-bold leading-tight">{titles[n] || `${n} day streak`}</h2>
          <p className="mt-3 text-sm text-zinc-300">
            You've shown up <span className="font-semibold text-white">{n}</span> days in a row.
            That consistency is the work.
          </p>
          {longest > n && (
            <p className="mt-2 text-xs text-zinc-500">Personal best: {longest} days</p>
          )}
          <button
            onClick={onClose}
            data-testid="streak-milestone-continue-btn"
            className="mt-5 w-full rounded-full h-12 bg-lime-400 text-black font-semibold flex items-center justify-center gap-2"
          >
            Keep the fire <Sparkles className="h-4 w-4" />
          </button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
